/**
 * Analysis Engine
 * Core fitness calculations without unnecessary complexity
 */

import { CategorizedOneRepMax, WeightAnalysisUI } from '../types'

const KCAL_PER_KG = 7700
const WEIGHT_CHANGE_THRESHOLD = 0.1 // kg per week
// Plateau detection threshold is now configurable via settings

const WEIGHT_ALPHA = 0.15
const ONE_RM_ALPHA = 0.5

// Date formatter helper - use local date to match parseLocalDate
const fmt = (d: Date) => {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ===== CORE UTILITY FUNCTIONS =====

/**
 * Simple 1RM calculation using Epley formula
 */
export const calculate1RM = (weight: number, reps: number): number => {
  if (weight <= 0 || reps <= 0) return 0
  return weight * (1 + reps / 30)
}

/**
 * Calculate simple progress percentage
 */
export const calculateProgress = (current: number, previous: number): number => {
  if (previous <= 0) return 0
  return ((current - previous) / previous) * 100
}

/**
 * Apply Exponential Weighted Moving Average (EWMA)
 */
export const applyEWMA = (values: number[], alpha = 0.15): number[] => {
  if (!values.length) return []

  const smoothed: number[] = [values[0]]
  for (let i = 1; i < values.length; i++) {
    smoothed[i] = alpha * values[i] + (1 - alpha) * smoothed[i - 1]
  }
  return smoothed
}

// ===== EXERCISE ANALYSIS =====

/**
 * Calculate smoothed 1RM trend for exercises
 */
export const calculateSmoothedTrend = (
  data: Array<{ oneRepMax: number; trend?: number; date: string }>
) => {
  if (!data.length) return { trendData: [] }

  const rawValues = data.map((point) => point.oneRepMax || 0)
  const smoothedValues = applyEWMA(rawValues, ONE_RM_ALPHA)

  // Mutate existing objects instead of cloning
  data.forEach((point, index) => {
    point.trend = smoothedValues[index]
  })

  return { trendData: data }
}

/**
 * Enhanced smoothed trend calculation for both weighted and bodyweight exercises
 */
export const calculateEnhancedSmoothedTrend = (
  data: Array<{
    oneRepMax: number
    trend?: number
    date: string
    maxReps?: number
    repsTrend?: number
    isBodyweight?: boolean
  }>
) => {
  if (!data.length) return { trendData: [] }

  // Check if this is a bodyweight exercise
  const isBodyweightExercise = data.some((point) => point.isBodyweight)

  if (isBodyweightExercise) {
    // For bodyweight exercises, smooth the reps values
    const rawReps = data.map((point) => point.maxReps || point.oneRepMax || 0)
    const smoothedReps = applyEWMA(rawReps, ONE_RM_ALPHA)

    // Mutate existing objects to add reps trend
    data.forEach((point, index) => {
      point.repsTrend = smoothedReps[index]
      point.trend = smoothedReps[index] // Also set trend for compatibility
    })
  } else {
    // For weighted exercises, use the existing 1RM smoothing
    const rawValues = data.map((point) => point.oneRepMax || 0)
    const smoothedValues = applyEWMA(rawValues, ONE_RM_ALPHA)

    data.forEach((point, index) => {
      point.trend = smoothedValues[index]
    })
  }

  return { trendData: data }
}

// ===== PROGRESSIVE OVERLOAD INDEX (POI) =====

export interface POIAnalysis {
  percentageChange: number
  hasEnoughData: boolean
  exerciseBreakdown: Array<{
    name: string
    percentageChange: number
    baselineKg: number
    recentKg: number
  }>
  exerciseCount: number
  dateRange: {
    start: Date
    end: Date
  }
}

/**
 * Calculate POI - analyzes ALL exercises from last 30 days
 * Returns weighted average based on exercise count and individual changes
 */
export const calculateProgressiveOverloadIndex = (data: CategorizedOneRepMax[]): number => {
  if (!data || data.length === 0) return 0

  // Calculate 30-day window
  const now = new Date()
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  let totalWeightedProgress = 0
  let totalWeight = 0

  // Process ALL exercises that have data in the last 30 days
  data.forEach((exercise) => {
    if (exercise.chartData.length < 2) return

    // Check if exercise has any data in the last 30 days
    const hasRecentData = exercise.chartData.some((point) => new Date(point.date) >= thirtyDaysAgo)
    if (!hasRecentData) return

    // Get smoothed trend data
    const { trendData } = calculateSmoothedTrend([...exercise.chartData])

    // Get data from 30 days ago (or earliest available)
    const sortedData = trendData.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    // Find baseline: value from 30 days ago or earliest available after that
    let baseline = 0
    let baselineDate = thirtyDaysAgo

    // First, try to find data from exactly 30 days ago or just after
    const dataFrom30DaysAgo = sortedData.find((point) => new Date(point.date) >= thirtyDaysAgo)

    if (dataFrom30DaysAgo) {
      baseline = dataFrom30DaysAgo.trend || 0
      baselineDate = new Date(dataFrom30DaysAgo.date)
    } else {
      // If no data from 30 days ago, use the most recent data before 30 days ago
      const olderData = sortedData.filter((point) => new Date(point.date) < thirtyDaysAgo).pop()

      if (olderData) {
        baseline = olderData.trend || 0
        baselineDate = new Date(olderData.date)
      } else {
        return // No valid baseline
      }
    }

    // Get most recent value
    const recentData = sortedData[sortedData.length - 1]
    const recent = recentData?.trend || 0
    const recentDate = new Date(recentData.date)

    // Calculate days between measurements for weighting
    const daysBetween = Math.max(
      1,
      (recentDate.getTime() - baselineDate.getTime()) / (24 * 60 * 60 * 1000)
    )

    if (baseline > 0 && daysBetween >= 1) {
      // At least 1 day between measurements
      const progress = calculateProgress(recent, baseline)

      // Weight by the number of data points in the period (more data = more reliable)
      const dataPointsInPeriod = sortedData.filter(
        (point) => new Date(point.date) >= baselineDate && new Date(point.date) <= recentDate
      ).length

      const weight = Math.log(dataPointsInPeriod + 1) // Logarithmic weighting
      totalWeightedProgress += progress * weight
      totalWeight += weight
    }
  })

  // Return weighted average progress
  return totalWeight > 0 ? totalWeightedProgress / totalWeight : 0
}

/**
 * Calculate POI with exercise breakdown for ALL exercises
 */
export const calculatePOI = (
  oneRepMaxes: CategorizedOneRepMax[],
  muscleGroup: string
): POIAnalysis => {
  const muscleData = oneRepMaxes.filter((d) => d.primaryMover === muscleGroup)
  const poi = calculateProgressiveOverloadIndex(muscleData)

  // Calculate date range
  const now = new Date()
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  // Calculate breakdown for ALL exercises with recent activity
  const exercisesWithProgress: Array<{
    name: string
    percentageChange: number
    baselineKg: number
    recentKg: number
  }> = []

  muscleData.forEach((exercise) => {
    if (exercise.chartData.length < 2) return

    // Check if exercise has any data in the last 30 days
    const hasRecentData = exercise.chartData.some((point) => new Date(point.date) >= thirtyDaysAgo)
    if (!hasRecentData) return

    const { trendData } = calculateSmoothedTrend([...exercise.chartData])
    const sortedData = trendData.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    // Find baseline: value from 30 days ago or earliest available
    let baseline = 0
    let baselineDate = thirtyDaysAgo

    const dataFrom30DaysAgo = sortedData.find((point) => new Date(point.date) >= thirtyDaysAgo)

    if (dataFrom30DaysAgo) {
      baseline = dataFrom30DaysAgo.trend || 0
      baselineDate = new Date(dataFrom30DaysAgo.date)
    } else {
      const olderData = sortedData.filter((point) => new Date(point.date) < thirtyDaysAgo).pop()

      if (olderData) {
        baseline = olderData.trend || 0
        baselineDate = new Date(olderData.date)
      } else {
        return
      }
    }

    // Get most recent value
    const recentData = sortedData[sortedData.length - 1]
    const recent = recentData?.trend || 0
    const recentDate = new Date(recentData.date)

    // Calculate days between measurements
    const daysBetween = (recentDate.getTime() - baselineDate.getTime()) / (24 * 60 * 60 * 1000)

    if (baseline > 0 && daysBetween >= 1) {
      const exercisePOI = calculateProgress(recent, baseline)
      exercisesWithProgress.push({
        name: exercise.name,
        percentageChange: exercisePOI,
        baselineKg: baseline,
        recentKg: recent
      })
    }
  })

  // Sort by percentage change and take top performers for display
  const exerciseBreakdown = exercisesWithProgress
    .sort((a, b) => b.percentageChange - a.percentageChange)
    .slice(0, 5) // Show top 5 for space constraints

  return {
    percentageChange: poi,
    hasEnoughData: exercisesWithProgress.length > 0,
    exerciseBreakdown,
    exerciseCount: exercisesWithProgress.length,
    dateRange: {
      start: thirtyDaysAgo,
      end: now
    }
  }
}

// ===== WEIGHT ANALYSIS =====

/**
 * Main Weight Analysis Return Function for Frontend Via useWeightAnalysis Hook
 * Always analyzes the full provided dataset for consistent trend calculations
 */
export const analyzeWeightData = (
  rawWeightData: Array<{ date: string; weight: number }>,
  plateauThreshold = 0.2 // kg over 14 days - default fallback
): WeightAnalysisUI => {
  if (rawWeightData.length === 0) {
    return {
      weightData: [],
      trendData: [],
      currentWeight: 0,
      weeklyTrendChange: 0,
      dailyCaloricBalance: 0,
      phase: 'insufficient_data',
      plateauDetected: false,
      hasValidTrend: false
    }
  }

  // Sort by date
  const sorted = [...rawWeightData].sort((a, b) => a.date.localeCompare(b.date))
  const dataMap = new Map(sorted.map((d) => [d.date, d.weight]))

  // Parse date helper
  const parseLocalDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const startDate = parseLocalDate(sorted[0].date)
  const lastDataDate = parseLocalDate(sorted[sorted.length - 1].date)
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Normalize to start of day
  // Use today as end date if we have data, or last data date if it's in the future
  const endDate = lastDataDate > today ? lastDataDate : today
  const allDatesForTrend: Array<{ date: string; weight: number }> = []

  // O(n) interpolation with two pointers
  const currentDate = new Date(startDate)
  let nextIdx = 1
  let prevWeight = sorted[0].weight
  let prevDate = startDate
  let nextWeight = sorted[Math.min(1, sorted.length - 1)].weight
  let nextDate = sorted.length > 1 ? parseLocalDate(sorted[1].date) : endDate

  while (currentDate <= endDate) {
    const dateStr = fmt(currentDate)
    const actualWeight = dataMap.get(dateStr)

    if (actualWeight !== undefined) {
      allDatesForTrend.push({ date: dateStr, weight: actualWeight })
      prevWeight = actualWeight
      prevDate = new Date(currentDate)

      // Advance next pointer if needed
      while (nextIdx < sorted.length && sorted[nextIdx].date <= dateStr) {
        nextIdx++
      }
      if (nextIdx < sorted.length) {
        nextWeight = sorted[nextIdx].weight
        nextDate = parseLocalDate(sorted[nextIdx].date)
      }
    } else {
      // Linear interpolation
      if (nextDate > prevDate) {
        const totalDays = (nextDate.getTime() - prevDate.getTime()) / (24 * 60 * 60 * 1000)
        const daysPassed = (currentDate.getTime() - prevDate.getTime()) / (24 * 60 * 60 * 1000)
        const weightDiff = nextWeight - prevWeight
        const interpolatedWeight = prevWeight + (weightDiff * daysPassed) / totalDays
        allDatesForTrend.push({ date: dateStr, weight: interpolatedWeight })
      } else {
        allDatesForTrend.push({ date: dateStr, weight: prevWeight })
      }
    }

    currentDate.setDate(currentDate.getDate() + 1)
  }

  // Apply single EWMA pass for trend
  const weightsForTrend = allDatesForTrend.map((d) => d.weight)
  const smoothedWeights = applyEWMA(weightsForTrend, WEIGHT_ALPHA)

  // Create trend data for all dates (including interpolated days)
  const trendData = allDatesForTrend.map((point, index) => ({
    date: point.date,
    weight: smoothedWeights[index]
  }))

  // Calculate metrics from TREND data only
  let weeklyTrendChange = 0
  let dailyCaloricBalance = 0
  let hasValidTrend = false

  // 7-day change: trendToday - trend7DaysAgo
  if (smoothedWeights.length >= 8) {
    const trendToday = smoothedWeights[smoothedWeights.length - 1]
    const trend7DaysAgo = smoothedWeights[smoothedWeights.length - 8]
    weeklyTrendChange = trendToday - trend7DaysAgo
    hasValidTrend = true
  }

  // 14-day caloric balance: (trendToday - trend14DaysAgo) * 7700 / 14
  if (smoothedWeights.length >= 15) {
    const trendToday = smoothedWeights[smoothedWeights.length - 1]
    const trend14DaysAgo = smoothedWeights[smoothedWeights.length - 15]
    const twoWeekChange = trendToday - trend14DaysAgo
    dailyCaloricBalance = (twoWeekChange * KCAL_PER_KG) / 14
  }

  // Simple phase detection based on weekly trend change
  let phase: 'bulking' | 'cutting' | 'maintenance' | 'insufficient_data' = 'insufficient_data'
  if (hasValidTrend) {
    if (weeklyTrendChange >= WEIGHT_CHANGE_THRESHOLD) phase = 'bulking'
    else if (weeklyTrendChange <= -WEIGHT_CHANGE_THRESHOLD) phase = 'cutting'
    else phase = 'maintenance'
  }

  // Plateau detection - simple 14-day comparison
  let plateauDetected = false
  if (smoothedWeights.length >= 15) {
    const trendToday = smoothedWeights[smoothedWeights.length - 1]
    const trend14DaysAgo = smoothedWeights[smoothedWeights.length - 15]
    plateauDetected = Math.abs(trendToday - trend14DaysAgo) < plateauThreshold
  }

  // Return unfiltered data - filtering happens at component level
  // Current weight from raw data (most recent actual measurement)
  const currentWeight = sorted[sorted.length - 1]?.weight || 0

  return {
    weightData: sorted, // Raw measurements
    trendData: trendData, // Full trend with interpolation
    currentWeight,
    weeklyTrendChange,
    dailyCaloricBalance,
    phase,
    plateauDetected,
    hasValidTrend
  }
}

/**
 * Filter weight data by time period (for client-side display filtering)
 * Used by components to zoom into specific periods without refetching data
 */
export const filterWeightDataByPeriod = (
  data: Array<{ date: string; weight: number }>,
  period: 'all' | '3months' | '1month' | '2weeks'
) => {
  if (period === 'all') return data

  const periods = { '3months': 90, '1month': 30, '2weeks': 14 }
  const days = periods[period]
  if (!days) return data

  const today = new Date()
  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - days)

  const todayStr = fmt(today)
  const cutoffStr = fmt(cutoffDate)

  return data.filter((item) => item.date >= cutoffStr && item.date <= todayStr)
}

// ===== EXERCISE PLATEAU DETECTION =====

/**
 * Detect if an exercise has plateaued based on 1RM trend over last 30 days
 * @param chartData Array of exercise data points with date and oneRepMax
 * @param plateauThreshold Percentage threshold for plateau detection (default 2%)
 * @returns true if exercise is plateauing
 */
export const detectExercisePlateau = (
  chartData: Array<{ date: string; oneRepMax: number }>,
  plateauThreshold = 2.0
): boolean => {
  if (!chartData || chartData.length < 2) return false

  // Get time windows
  const now = new Date()
  const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  // Get smoothed trend data
  const { trendData } = calculateSmoothedTrend([...chartData])

  // Get the most recent data point
  const mostRecentPoint = trendData[trendData.length - 1]
  if (!mostRecentPoint) return false

  // Filter for comparison data points between 14-30 days ago
  const comparisonData = trendData.filter((point) => {
    const pointDate = new Date(point.date)
    return pointDate >= thirtyDaysAgo && pointDate <= fourteenDaysAgo
  })

  // No valid comparison data in the 14-30 day window
  if (comparisonData.length === 0) return false

  // Use the most recent point in the comparison window
  const comparisonPoint = comparisonData[comparisonData.length - 1]
  const trendToday = mostRecentPoint.trend
  const trendComparison = comparisonPoint.trend

  if (!trendToday || !trendComparison || trendComparison <= 0) return false

  // Calculate percentage change
  const changePercentage = ((trendToday - trendComparison) / trendComparison) * 100

  // Return true if change is below threshold (plateauing)
  return Math.abs(changePercentage) < plateauThreshold
}

export const getExercisePlateauDetails = (
  chartData: Array<{ date: string; oneRepMax: number }>,
  plateauThreshold = 2.0
): {
  isPlateauing: boolean
  trend30DaysAgo: number
  trendToday: number
  changePercentage: number
  baseline30DaysAgo: Date
  recentDate: Date
  plateauThreshold: number
} | null => {
  if (!chartData || chartData.length < 2) return null

  // Get time windows
  const now = new Date()
  const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  // Get smoothed trend data
  const { trendData } = calculateSmoothedTrend([...chartData])

  // Get the most recent data point
  const mostRecentPoint = trendData[trendData.length - 1]
  if (!mostRecentPoint) return null

  // Filter for comparison data points between 14-30 days ago
  const comparisonData = trendData.filter((point) => {
    const pointDate = new Date(point.date)
    return pointDate >= thirtyDaysAgo && pointDate <= fourteenDaysAgo
  })

  // No valid comparison data in the 14-30 day window
  if (comparisonData.length === 0) return null

  // Use the most recent point in the comparison window
  const comparisonPoint = comparisonData[comparisonData.length - 1]
  const trendToday = mostRecentPoint.trend
  const trendComparison = comparisonPoint.trend
  const baselineDate = new Date(comparisonPoint.date)
  const recentDate = new Date(mostRecentPoint.date)

  if (!trendToday || !trendComparison || trendComparison <= 0) return null

  // Calculate percentage change
  const changePercentage = ((trendToday - trendComparison) / trendComparison) * 100

  return {
    isPlateauing: Math.abs(changePercentage) < plateauThreshold,
    trend30DaysAgo: trendComparison,
    trendToday,
    changePercentage,
    baseline30DaysAgo: baselineDate,
    recentDate,
    plateauThreshold
  }
}
